import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { customerService } from '../services/customerService';

const CustomerContext = createContext();

export { CustomerContext };

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar clientes da empresa do usuário logado
  const fetchCustomers = async () => {
    if (!user?.companyId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getByCompany(user.companyId);
      setCustomers(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Adicionar novo cliente
  const addCustomer = async (customerData) => {
    if (!user?.companyId) throw new Error('Usuário não está associado a uma empresa');
    
    try {
      const newCustomer = await customerService.create({
        name: customerData.name,
        cpf: customerData.cpf,
        birthDate: customerData.birthDate,
        companyId: user.companyId
      });
      setCustomers(prev => [...prev, newCustomer]);
      return newCustomer;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Atualizar cliente
  const updateCustomer = async (customerId, customerData) => {
    try {
      const updatedCustomer = await customerService.update(customerId, customerData);
      setCustomers(prev => 
        prev.map(customer => 
          customer.id === customerId ? updatedCustomer : customer
        )
      );
      return updatedCustomer;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Deletar cliente
  const deleteCustomer = async (customerId) => {
    try {
      await customerService.delete(customerId);
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Buscar cliente por CPF (para consulta pública)
  const findCustomerByCpf = async (cpf) => {
    try {
      const customer = await customerService.getByCpf(cpf);
      return customer;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Atualizar pontos do cliente
  const updateCustomerPoints = (customerId, pointsToAdd) => {
    setCustomers(prev => 
      prev.map(customer => 
        customer.id === customerId 
          ? { ...customer, points: customer.points + pointsToAdd }
          : customer
      )
    );
  };

  // Buscar clientes quando o usuário muda
  useEffect(() => {
    if (user?.companyId) {
      fetchCustomers();
    } else {
      setCustomers([]);
    }
  }, [user?.companyId]);

  const value = {
    customers,
    loading,
    error,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    findCustomerByCpf,
    updateCustomerPoints,
    refreshCustomers: fetchCustomers
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerProvider;